import image from '@/assets/img/icons/thumb.svg'
import format from 'date-fns/format'
import ruLocale from 'date-fns/locale/ru'
import 'chartjs-plugin-colorschemes'
import ModalAlert from '@/components/modals/ModalAlert'

/**
 *  Миксин с общими свойствами и методами для календаря.
 */
export const datepickerMixin = {
  data() {
    return {
      /**
       *  Даты начала и завершения в формате YYYY-MM-DD.
       */
      sDate: '',
      eDate: '',
      /**
       *  Формат даты выводимой в инпуте календаря при выборе даты.
       */
      dateFormat: 'D MMM',
      /**
       *  Кнопки `Закрыть/Применить` внутри календаря по умолчанию не отображаются.
       */
      showActionButtons: false,
      /**
       *  Ограничиваем период доступных дат в календаре шестью месяцами.
       */
      minDate: new Date(Date.now() - 86400000 * 180),
      maxDate: new Date(),
      /**
       *  Отображаем в календаре один месяц.
       */
      monthsToShow: 1,
    }
  },
  methods: {
    /**
     * Приводим выбранные даты к формату `D MMM - D MMM` в русской локале и отображаем в инпуте.
     * После выбора даты завершения внутри календаря появляются кнопки `Закрыть/Применить`.
     * @param {String} sDate Дата начала в формат `YYYY-MM-DD`.
     * @param {String} eDate Дата завершения в формат `YYYY-MM-DD`.
     * @returns {String}
     */
    formatDates(sDate, eDate) {
      let formattedDates = ''
      if (sDate) {
        formattedDates = format(sDate, this.dateFormat, { locale: ruLocale })
      }
      if (eDate) {
        formattedDates +=
          ' - ' + format(eDate, this.dateFormat, { locale: ruLocale })
        this.showActionButtons = true
      }
      return formattedDates
    },
  },
}

/**
 *  Миксин для определения дополнительной логики при выборе элементов из select.
 */
export const selectMixin = {
  data() {
    return {
      /**
       * В зависимости от значения данного свойства вызываются различные методы api
       * на страницах `Выгрузка` и `Статистика`.
       */
      modeAllObjects: false,
    }
  },
  methods: {
    /**
     * При выборе элементов из select по умолчанию логика работы компонента такова,
     * что в единицу времени возможен выбор элемента `Все` и также других адресов.
     * Также возможна ситуация когда были выбраны элементы и затем выбранных элементов не стало вовсе.
     * Такие ситации должны быть исключены, так как при каждом изменении набора элементов отправляется запрос к БД
     * согласно принятой логики работы UI, и такие наборы данных вызовут ошибки при обращении к api.
     * @param {Array.<Number|String>} array Массив выбранных элементов из select.
     * @param {Boolean} flag Специальный флаг для отображения только одного (выбранного последним) адреса.
     *                       Имеет место быть только при нажатой вкладке `Нагрузка по дням недели`.
     *                       То есть по сути значение true отменяет множественный выбор.
     *                       Манипуляция со значениями для свойства `multiple` результата не даст, так как
     *                       тип данных отправляемых на сервер измениться.
     */
    selectObjects(array, flag) {
      /**
       * Если массив содержал n-количество элементов и при очередном выборе нового элемента в массив добавляется
       * элемент `Все`, то в select устанавливается только это значение.
       */
      if (array.includes('Все') && array[0] !== 'Все') {
        this.objectsSelected = ['Все']
        this.modeAllObjects = true
        return false
        /**
         * Если при очередном выборе нового элемента первым элементом в массиве является элемент `Все`,
         * то необходимо исключить его из результирующего массива.
         */
      } else if (array[0] === 'Все') {
        array.splice(0, 1)
        this.modeAllObjects = false
        return false
        /**
         * Если попытаться отменить выбор ранее выбранного элемента, нажав на него ещё раз и при этом
         * больше не будет выбранных элементов, то по умолчанию устанавливаем выбранным элемент `Все`.
         */
      } else if (!array.length) {
        this.objectsSelected = ['Все']
        this.modeAllObjects = true
        return false
      }
      if (flag) {
        array.splice(0, 1)
        return false
      }
    },
  },
}

/**
 *  Миксин с общими свойствами и методами для работы с шаблонами страниц.
 */
export const templateMixin = {
  methods: {
    /**
     *  Общая функция валидации.
     *  @returns {Undefined}
     */
    validateForm() {
      /**
       *  Управляем доступностью кнопки `Сохранить`.
       */
      if (this.pageBackgroundName && this.pageName) {
        this.disabledButton = false
      } else {
        this.disabledButton = true
      }
    },
    /**
     *  Проверяем тип загружаемого файла.
     *  @param {String} type MIME-тип загружаемого файла.
     *  @param {String} target Значение ref атрибута.
     *  @returns {Undefined}
     */
    validateUploadFile(type, target) {
      if (
        type.indexOf('png') === -1 &&
        type.indexOf('jpg') === -1 &&
        type.indexOf('jpeg') === -1
      ) {
        this.resetImage(target)
        this.isValidFile = false
        this.$vuedals.open({
          name: 'vuedal_custom',
          component: ModalAlert,
          props: {
            title: 'Ошибка',
            message: `<p class='error'>Загружаемый файл должен быть в следующих форматах:<br>png, jpg, jpeg.</p>`,
          },
          dismissable: false,
          escapable: true,
        })
      }
    },
    /**
     * Если валидация не прошла, то сбрасываем значения по умолчанию.
     * @param {String} target Значение ref атрибута.
     * @returns {Undefined}
     */
    resetImage(target) {
      if (target === 'pageBackground') {
        this.backgroundInitial = ''
      } else {
        this.logo = ''
      }
    },
  },
}

/**
 *  Миксин для работы контекстного меню у шаблонов и привязок шаблонов к адресам договора.
 */
export const contexMenuMixin = {
  data() {
    return {
      /**
       *  Идентификатор шаблона или привязки шаблона, по элементу управления которого произошло
       *  нажатие. Если значением является неотрицательное число, то отображается контексное меню для того блока,
       *  по элементу управления которого произошло нажатие.
       *  По умолчанию равно -1. Присваивание такого значения позволяет скрыть контекстное меню у блока.
       */
      openItemID: -1,
      /**
       *  В случае если для шаблона или привязки шаблона нет превью,
       *  то вместо него выводим специальную иконку.
       */
      image: image,
    }
  },
  mounted() {
    /**
     *  При клике вне контекстного меню (за исключением кнопки открывающей меню)
     *  и по пунктам меню скрываем его.
     */
    document.body.addEventListener('click', (e) => {
      if (
        e.target.id === 'btnShowContexMenu' ||
        e.target.classList.contains('icon-more')
      ) {
        return false
      } else {
        this.openItemID = -1
      }
    })
  },
  methods: {
    /**
     * Отображаем контекстное меню.
     * @param {Number} id Идентификатор шаблона или привязок шаблона к адресу договора.
     */
    showContextMenu(id) {
      if (this.openItemID === id) {
        this.openItemID = -1
      } else {
        this.openItemID = id
      }
    },
  },
}

/**
 *  Миксин для построения графиков.
 */
export const chartConfigMixin = {
  props: {
    dataChart: {
      type: Array,
      required: true,
      default: () => {
        return []
      },
    },
    period: {
      type: String,
      required: true,
      default: 'month',
    },
    typeDetailSelected: {
      type: String,
    },
  },
  computed: {
    /**
     *  Общее количество подключений посетителей (используется для графиков `Все посетители` и `Уникальные посетители`).
     *  @returns {Number}
     */
    visitorsCount() {
      return this.dataChart.reduce((accumulator, currentValue) => {
        const visitorsCountByOneAddress = currentValue.statistic.reduce(
          (accumulator, currentValue) => accumulator + currentValue.count,
          0
        )
        return accumulator + visitorsCountByOneAddress
      }, 0)
    },
  },
  data() {
    return {
      /**
       *  Объект графика.
       */
      chart: null,
      chartOptions: {
        /**
         *  Отключаем сохранение пропорций блока с графиком при изменении
         *  размера окна браузера для обеспечения лучшего отображения.
         */
        maintainAspectRatio: false,
        /**
         *  Настройки для управления смещением слоя с графиком
         *  относительно родительского элемента.
         */
        layout: {
          padding: {
            left: -20,
          },
        },
        /**
         *  Настройки для легенды.
         */
        legend: {
          position: 'bottom',
          labels: {
            fontSize: 12,
            fontStyle: 'normal',
            padding: 30,
            usePointStyle: true,
          },
          onClick: null,
        },
        /**
         *  Настройки для заголовка графика.
         */
        title: {
          display: true,
          fontSize: 18,
          fontColor: '#333',
          fontStyle: 'normal',
        },
        /**
         *  Настройки для тултипов при наведении на точки на графике.
         */
        tooltips: {
          backgroundColor: 'white',
          titleFontColor: '#333',
          bodyFontColor: '#333',
          borderColor: '#8f8f8f',
          displayColors: false,
          borderWidth: 1,
          xPadding: 10,
          yPadding: 10,
          caretPadding: 6,
          caretSize: 10,
          titleSpacing: 8,
          bodySpacing: 5,
          callbacks: {
            title(tooltipItem, data) {
              return data.datasets[tooltipItem[0].datasetIndex].label
            },
            afterLabel(tooltipItem, data) {
              return `Количество посещений: ${data.datasets[tooltipItem.datasetIndex].data[tooltipItem['index']].y}`
            },
          },
        },
        scales: {
          /**
           *  Настройки для отображения оси X.
           */
          xAxes: [
            {
              type: 'time',
              display: true,
              offset: true,
              gridLines: {
                display: true,
                drawOnChartArea: false,
              },
              time: {
                unit: 'day',
                displayFormats: {
                  day: 'MMM DD YYYY',
                },
              },
              ticks: {
                callback: (value) => {
                  if (
                    this.period === 'today' ||
                    this.period === 'yesterday' ||
                    (this.period === 'week' &&
                      this.typeDetailSelected === 'hour') ||
                    this.typeDetailSelected === 'hour' ||
                    this.typeDetailSelected === 'tenmin' ||
                    this.typeDetailSelected === 'minute'
                  ) {
                    return value
                  } else {
                    return new Date(value).toLocaleDateString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })
                  }
                },
              },
            },
          ],
          /**
           *  Настройки для отображения оси Y.
           */
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
              },
              ticks: {
                precision: 0,
                padding: 10,
              },
            },
          ],
        },
        plugins: {
          /**
           *  Применяем цветовую тему из плагина.
           *  @see{@link https://github.com/nagix/chartjs-plugin-colorschemes}
           */
          colorschemes: {
            scheme: 'brewer.Paired12',
          },
          datalabels: {
            display: false,
          },
        },
      },
    }
  },
  methods: {
    /**
     *  В зависимости от периода необходимо отображать разные данные по оси X.
     *  @returns {Undefined}
     */
    getAxesLabel() {
      if (
        this.period === 'today' ||
        this.period === 'yesterday' ||
        this.typeDetailSelected === 'hour' ||
        this.typeDetailSelected === 'tenmin' ||
        this.typeDetailSelected === 'minute'
      ) {
        this.chart.options.scales.xAxes[0].time.unit = 'hour'
        this.chart.options.scales.xAxes[0].time.displayFormats = {
          hour: 'HH:mm',
        }
        this.chart.options.tooltips.callbacks.label = (tooltipItem, data) => {
          let date = new Date(
            data.datasets[tooltipItem.datasetIndex].data[tooltipItem['index']].x
          ).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
          let time = new Date(
            data.datasets[tooltipItem.datasetIndex].data[tooltipItem['index']].x
          ).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
          return `Дата, время: ${date}, ${time}`
        }
      } else {
        this.chart.options.scales.xAxes[0].time.unit = 'day'
        this.chart.options.scales.xAxes[0].time.displayFormats = {
          day: 'MMM DD YYYY',
        }
        this.chart.options.tooltips.callbacks.label = (tooltipItem, data) => {
          let date = new Date(
            data.datasets[tooltipItem.datasetIndex].data[tooltipItem['index']].x
          ).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })
          return `Дата: ${date}`
        }
      }
    },
    /**
     *  Получаем данные для построения точек на графике.
     *  @returns {Array}
     */
    getDatasets() {
      let datasets = []
      this.dataChart.forEach((item) => {
        let labels = item.statistic.map((item) => {
          return {
            x: item.date,
            y: item.count,
          }
        })
        let dataset = {
          label: item.address,
          data: labels,
          fill: false,
          hoverBorderWidth: 4,
          borderWidth: 2,
        }
        datasets.push(dataset)
      })
      return datasets || []
    },
  },
}

/**
 *  Миксин для вывода ошибок с сервера.
 */
export const errorMixin = {
  methods: {
    /**
     *  Общий метод для вывода ошибок с сервера.
     *  @param {Object} event Объект ошибки.
     *  @returns {Undefined}
     */
    responseError(event) {
      this.$vuedals.close()
      let errorTitle = ''
      let errorText = event.response.data.detail || event.response.data
      /**
       *  Отдельно обрабатывается ошибка при истечение времени жизни токена.
       */
      if (event.message.indexOf('401') !== -1) {
        errorTitle = 'Внимание'
        errorText = 'Время сессии истекло. Пожалуйста, авторизуйтесь снова.'

        this.$store.commit('LOGOUT')
        this.$router.push({ name: 'Auth' })
      }
      /**
       *  Если нет подключенных объектов, то при переходе на страницы 'Реклама', 'Переадресация', 'Выгрузка'
       *  будет выдаваться 403я ошибка от сервера. Тут ловим такой ответ, чтобы не выводить модальное окно,
       *  так как в нём нет необходимости.
       */
      if (event.message.indexOf('403') === -1) {
        this.$vuedals.open({
          name: 'vuedal_custom',
          component: ModalAlert,
          props: {
            title: errorTitle || 'Ошибка',
            message: errorText || event.message,
          },
          dismissable: false,
          escapable: true,
        })
      }
    },
  },
}

/**
 *  Опция для проверки есть ли подключенные объекты на договоре.
 *  Если нет подключенных объектов, то при переходе на страницы
 *  'Реклама', 'Переадресация', 'Выгрузка' отображается заглушка.
 */
export const checkСonnectedObjectsExistMixin = {
  computed: {
    isConnectedObjectsExist() {
      if (this.$store.state.objects) {
        return this.$store.state.objects.some(
          (object) => object.lk_status === 1
        )
      }
    },
  },
}

/**
 * В зависимости от ширины экрана устройства отображаются
 * разные элементы в шапке и изменяется навигация.
 */
export const checkMobileMixin = {
  data() {
    return {
      isMobileDevice: false,
    }
  },
  methods: {
    /**
     * Проверить ширину экрана устройства.
     * @returns {Undefined}
     */
    checkDeviceWidth() {
      let width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth
      if (width <= 767) {
        this.isMobileDevice = true
      } else {
        this.isMobileDevice = false
      }
    },
    /**
     * При монтировании всякого представления/компонента,
     * внутри которого используется `isMobileDevice`, необходимо вызывать данный метод.
     * В зависимости от значения isMobileDevice, в UI рендерятся различные элементы.
     * Также, что очень важно, меняется роутинг в приложении.
     * @returns {Undefined}
     */
    watchDeviceWidth() {
      this.checkDeviceWidth()
      window.addEventListener('resize', () => {
        this.checkDeviceWidth()
      })
      /**
       * При увеличении ширины окна браузера (в том числе путём смены ориентации устройства)
       * и при условии, что пользователь находиться на странице со списком разделов,
       * отображаем страницу `Статистика`.
       */
      if (!this.isMobileDevice && this.$route.name === 'HomeMobile') {
        this.$router.push({ name: 'Statistics' })
      }
    },
  },
}

/**
 *  В случае если произошли какие-то изменения в системе,
 *  то оповещаем об этом пользователя.
 *
 *  Пока сделано очень просто. Окно с изменениями может быть только одно,
 *  общее на все изменения.
 *
 *  Чтобы не выводить данное окно постоянно используется специальное свойство в сторе,
 *  а также дата завершения показа данного окна (назначается заказчиком).
 */
export const checkSystemChangesMixin = {
  methods: {
    checkCurrentDate() {
      let timeNow = Date.now()
      const timeEnd = Date.parse(new Date(2021, 2, 18))
      if (this.$store.state.isShowModal && timeEnd > timeNow) {
        this.showModalWithChanges()
      }
    },
    /**
     *  Отобразить модальное окно с текстом об изменениях в системе.
     *  @returns {Undefined}
     */
    showModalWithChanges() {
      this.$vuedals.open({
        name: 'vuedal_custom',
        component: ModalAlert,
        props: {
          title: 'Новости от разработчиков',
          message: `<p style="margin-bottom: 5px;"><b>Уважаемый клиент! Для вас обновления:</b></p>
            <p style="margin-bottom: 10px;">
              🌈 В разделе <b>"Страница приветствия"</b> при настройке персональных стартовых страниц Wi-Fi
              теперь можно выбирать порядок их отображения: в зависимости от времени суток, дня недели или в формате 24/7.
            </p>
            <p>
              🌈 В разделе <b>"Статистика"</b> появилась информация о количестве подключений к Wi-Fi в разбивке по дням недели.
              Это поможет отслеживать динамику посещаемости Вашего заведения.
            </p>`,
          buttonText: 'Понятно',
          isVisibleFooter: true,
          footerClass: 'message-box__footer--center',
          modalName: 'news-devs',
        },
        dismissable: false,
        escapable: true,
      })
    },
  },
}



// WEBPACK FOOTER //
// ./src/mixins/index.js